import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Grow, IconButton, Stack } from '@mui/material';
import { memo, ReactElement, useState } from 'react';
import { toggle } from '../../../utils';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../../ApiLoadingGuard';
import { MaterialChip } from './MaterialChip';
import { UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '../../ApiLoadingGuard/ApiLoadingGuard';
import { ResourceStatus } from '../../../api/resources/useOcInsightsEvents';
import { DefaultWaitingScreen } from '../../ApiLoadingGuard/DefaultWaitingScreen';
import { MaterialClassification } from '../../../api/queries/materialClassification';

export interface MaterialColumnQueryGuardedProps {
  materialClassificationQuery: UseQueryResult<MaterialClassification, ApiError>;
}

export function MaterialColumnQueryGuarded({ materialClassificationQuery }: MaterialColumnQueryGuardedProps) {
  const { isPending, isEnabled, isError, data } = materialClassificationQuery;
  const isNotAvailable = isError || !isEnabled;

  if (isNotAvailable) {
    return <DefaultErrorScreen defaultText={'error.predictionNotAvailable'} iconSize='medium' />;
  }

  if (isPending) {
    return <DefaultLoadingScreen />;
  }

  return <MaterialColumn transformedData={data} />;
}

export interface LiveDataMaterialColumnQueryGuardedProps {
  materialClassificationQuery: UseQueryResult<MaterialClassification, ApiError>;
  resourceStatus: ResourceStatus;
}

export function LiveDataMaterialColumnQueryGuarded({
  materialClassificationQuery,
  resourceStatus,
}: LiveDataMaterialColumnQueryGuardedProps) {
  if (resourceStatus === 'not-started') {
    return <DefaultWaitingScreen text='dataLoading.idle' />;
  }

  if (resourceStatus === 'started') {
    return <DefaultLoadingScreen text='dataLoading.processing' />;
  }

  return <MaterialColumnQueryGuarded materialClassificationQuery={materialClassificationQuery} />;
}

export interface MaterialColumnProps {
  transformedData: MaterialClassification;
}

export const MaterialColumn = memo(function ({ transformedData }: MaterialColumnProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  const { mainChip, expandableChips } = transformedData;

  return (
    <Box
      paddingLeft={1}
      paddingRight={1}
      paddingBottom={1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      {mainChip && (
        <MaterialChip
          primaryText={mainChip.primaryText}
          secondaryText={mainChip.secondaryText}
          probability={mainChip.probability}
        />
      )}
      {expandableChips && (
        <Box width='100%' display='flex' flexDirection='column' alignItems='center'>
          <IconButton onClick={toggle(isExpanded, setIsExpanded)}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Grow in={isExpanded}>
            <Stack spacing={1} alignItems='center' sx={{ width: '100%' }}>
              {expandableChips.map((chip, index) => (
                <MaterialChip
                  key={index}
                  primaryText={chip.primaryText}
                  secondaryText={chip.secondaryText}
                  probability={chip.probability}
                />
              ))}
            </Stack>
          </Grow>
        </Box>
      )}
    </Box>
  );
});
